import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faCircleXmark, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { Subject, takeUntil, filter, tap } from 'rxjs';
import { HistoryService } from './services/history.service';
import { FormatValuePipe } from './services/format-value.pipe';
import { HistoryItem, HistoryTrack } from './models/history.model';

@Component({
  selector: 'app-history',
  standalone: true,
  imports: [CommonModule, TranslateModule, FontAwesomeModule, FormatValuePipe, DatePipe],
  templateUrl: './history.component.html',
  styleUrls: ['./history.component.scss']
})
export class HistoryComponent implements OnInit, OnDestroy {
  protected readonly faCircleXmark = faCircleXmark;
  protected readonly faArrowRight = faArrowRight;

  historyData: HistoryItem[] = [];
  fieldsMetadata: Record<string, { title: string; type: string }> = {};
  isLoading = true;

  private destroy$ = new Subject<void>();

  constructor(private historyService: HistoryService) {}

  ngOnInit(): void {
    this.subscribeLoading();
    this.subscribeToHistoryData();
    this.subscribeToFieldsMetadata();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private subscribeToHistoryData(): void {
    this.historyService.historyData$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (data) => {
        this.historyData = data;
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading history data:', error);
        this.isLoading = false;
      }
    });
  }

  private subscribeToFieldsMetadata(): void {
    this.historyService.fieldsData$
      .pipe(
        takeUntil(this.destroy$),
        filter((data) => !!data)
      )
      .subscribe({
        next: (metadata) => {
          this.fieldsMetadata = metadata;
        },
        error: (error) => {
          console.error('Error loading fields metadata:', error);
        }
      });
  }

  private subscribeLoading() {
    this.historyService.loading$.pipe(takeUntil(this.destroy$)).subscribe((loading) => {
      this.isLoading = loading;
    });
  }

  hasDisplayableTracks(item: HistoryItem): boolean {
    if (!item.tracks || !Array.isArray(item.tracks)) {
      return false;
    }
    return item.tracks.some((track) => this.shouldDisplayTrack(track));
  }

  getParsedBody(bodyStr: string): { title: string; tracks: HistoryTrack[] } | null {
    try {
      return JSON.parse(bodyStr);
    } catch {
      return null;
    }
  }

  getItemTracks(item: HistoryItem): HistoryTrack[] {
    if (item.tracks) {
      return item.tracks;
    }

    if (typeof item.body === 'string') {
      const parsed = this.getParsedBody(item.body);
      return parsed?.tracks || [];
    }

    return [];
  }

  getItemTitle(item: HistoryItem): string {
    if (item.title) {
      return item.title;
    }

    if (typeof item.body === 'string') {
      const parsed = this.getParsedBody(item.body);
      return parsed?.title || '';
    }

    return '';
  }

  getFieldTitle(fieldName: string): string {
    return this.fieldsMetadata[fieldName]?.title || fieldName;
  }

  shouldDisplayTrack(track: HistoryTrack): boolean {
    if (!track || !track.name) return false;

    const systemFields = [ 'version'];
    if (systemFields.includes(track.name)) return false;

    return !!(track.oldValue || track.value || track.oldDisplayValue || track.displayValue);
  }

  getTimeDifference(item: any): string {
    const creatingDateTrack = item.tracks.find(
      (t: HistoryTrack) => t.name === 'creatingDate' || t.name === 'createdOn'
    );
    const updatedOnTrack = item.tracks.find((t: HistoryTrack) => t.name === 'updatedOn');

    const track = creatingDateTrack || updatedOnTrack;

    if (!track) {
      return '';
    }

    const currentTime = new Date();
    const trackDateTime = new Date(track.value);
    const timeDifferenceInMilliseconds = currentTime.getTime() - trackDateTime.getTime();

    const seconds = Math.floor(timeDifferenceInMilliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const months = Math.floor(days / 30);
    const years = Math.floor(months / 12);

    const parts = [];

    if (years > 0) {
      parts.push(`${years} ${this.formatPlural(years, 'год', 'года', 'лет')}`);
      const remainingMonths = months % 12;
      if (remainingMonths > 0) {
        parts.push(`${remainingMonths} ${this.formatPlural(remainingMonths, 'месяц', 'месяца', 'месяцев')}`);
      }
    } else if (months > 0) {
      parts.push(`${months} ${this.formatPlural(months, 'месяц', 'месяца', 'месяцев')}`);
      const remainingDays = days % 30;
      if (remainingDays > 0) {
        parts.push(`${remainingDays} ${this.formatPlural(remainingDays, 'день', 'дня', 'дней')}`);
      }
    } else if (days > 0) {
      parts.push(`${days} ${this.formatPlural(days, 'день', 'дня', 'дней')}`);
      const remainingHours = hours % 24;
      if (remainingHours > 0) {
        parts.push(`${remainingHours} ${this.formatPlural(remainingHours, 'час', 'часа', 'часов')}`);
      }
    } else if (hours > 0) {
      parts.push(`${hours} ${this.formatPlural(hours, 'час', 'часа', 'часов')}`);
      const remainingMinutes = minutes % 60;
      if (remainingMinutes > 0) {
        parts.push(`${remainingMinutes} ${this.formatPlural(remainingMinutes, 'минута', 'минуты', 'минут')}`);
      }
    } else if (minutes > 0) {
      parts.push(`${minutes} ${this.formatPlural(minutes, 'минута', 'минуты', 'минут')}`);
    } else {
      parts.push(`${seconds} ${this.formatPlural(seconds, 'секунда', 'секунды', 'секунд')}`);
    }

    return `${parts.join(' ')} назад`;
  }
  formatPlural(value: number, single: string, plural2: string, plural5: string): string {
    const lastDigit = value % 10;
    const lastTwoDigits = value % 100;

    if (lastTwoDigits >= 11 && lastTwoDigits <= 19) {
      return plural5;
    } else if (lastDigit === 1) {
      return single;
    } else if (lastDigit >= 2 && lastDigit <= 4) {
      return plural2;
    } else {
      return plural5;
    }
  }

  trackById(index: number, item: HistoryItem): number {
    return item.id;
  }

  trackByName(index: number, track: HistoryTrack): string {
    return track.name;
  }
}
